import React, { useEffect } from 'react';
import { PAYMENT_STATUS } from '~../__queries__';
import { useStorage } from '~Components/Storage';
import { useTableApi, useTableUniverse } from '~Components/TableUniverse';
import { useDispatch, useLogger, useSelector } from '~Hooks';
import { useDispatchPrintReceipt } from '~Hooks/useReceiptCounter';
import { CreatePOSOrderSubPayments, POSPayment } from '~reducers';
import * as actions from '~actions';
import { useBookings } from '~Providers/BookingsProvider';

interface Props {
  payment: POSPayment;
  setPrintQuestionModal?: (print: boolean) => void;
}

const PaymentHandler: React.FunctionComponent<Props> = ({ payment, setPrintQuestionModal }) => {
  const dispatch = useDispatch();
  const api = useTableApi();
  const privateApi = useTableApi({
    private: true,
  });
  const logger = useLogger('payment');
  const bookings = useBookings();
  const dispatchPrintReceipt = useDispatchPrintReceipt();
  const [storageData, updateStorage] = useStorage();

  const dataRef = React.useRef(storageData);
  useEffect(() => {
    dataRef.current = storageData;
  }, [storageData]);

  const universe = useTableUniverse();
  const allCheques = universe.cheques;

  // Get all orders that belong to a cheque
  const orders = useSelector((state) => state.POSOrders.filter((order) => order.chequeId));

  useEffect(() => {
    const order = orders.find((o) => o.id === payment.order);
    const cheque = allCheques.find((c) => c.id === order?.chequeId);

    // If this is not actually a TSR payment, don't log
    if (!cheque) {
      return;
    }

    logger.info(`TSR: Payment created. Method: ${payment.method}`, {
      id: payment.id,
      method: payment.method,
      payment,
    });
  }, []);

  useEffect(() => {
    (async () => {
      const order = orders.find((o) => o.id === payment.order);
      const cheque = allCheques.find((c) => c.id === order?.chequeId);
      const chequePayments = universe.payments.filter((p) => p.chequeId === cheque?.id);

      // If this is not actually a TSR payment, dont do any of this
      if (!cheque) {
        return;
      }

      if (payment.status === 'FAILED' || payment.status === 'COMPLETED') {
        const message = payment?.message || '';
        logger.info(
          `TSR: Payment ${payment.status.toLowerCase()} (${payment.id}). ${payment.status ===
            'FAILED' && message}`,
          {
            id: payment.id,
          },
        );

        setTimeout(() => {
          dispatch({
            type: 'REMOVE_POS_PAYMENT',
            id: payment.id,
          });

          dispatch({
            type: 'REMOVE_POS_ORDER',
            id: order?.id,
          });
        }, 1000);
      }

      if (payment.status !== 'COMPLETED') {
        return;
      }

      if (order && cheque) {
        const chequeTotal = cheque.amount - cheque.discount;

        const amount = payment.amount ?? chequeTotal - cheque.paidAmount;

        if (cheque.tableId?.startsWith('POS_')) {
          privateApi.addPayment(
            order.chequeId!,
            amount,
            payment.method,
            payment.meta.tip ?? 0,
            payment.meta?.transactionId,
          );
        } else {
          api.addPayment(
            order.chequeId!,
            amount,
            payment.method,
            payment.meta.tip ?? 0,
            payment.meta?.transactionId,
          );
        }

        const isFullPayment = amount >= chequeTotal - cheque.paidAmount;
        api.log.addPayment(
          order.chequeId!,
          amount,
          payment.method,
          payment.meta.tip ?? 0,
          isFullPayment,
        );

        if (isFullPayment) {
          api.removeTableNote(cheque.tableId || '');

          if (storageData.askToPrint && setPrintQuestionModal) {
            setPrintQuestionModal(true);
          }

          const subPayments =
            payment.amount !== chequeTotal
              ? [
                  // Get all payments that belong to this cheque, the order.subPayments can be stale depending on when payment was created
                  ...chequePayments
                    // Remove the payment that was just created if it exists
                    .filter((p) => p.transactionId !== payment.meta.transactionId)
                    // Map to sub payments
                    .map(
                      (p): CreatePOSOrderSubPayments => ({
                        amount: p.amount,
                        status: PAYMENT_STATUS.SUCCESS,
                        transactionId: p.transactionId || p.id,
                        type: p.type,
                        tip: p.tip,
                      }),
                    ),
                  {
                    transactionId: payment.meta.transactionId,
                    type: payment.method,
                    amount: payment.amount,
                    status: PAYMENT_STATUS.SUCCESS,
                    tip: payment.meta.tip,
                  },
                ]
              : undefined;

          // Calculate total tip from sub payments if they exist
          let totalTip;
          if (subPayments) {
            totalTip = subPayments.reduce((sum, p) => (sum += p.tip || 0), 0);
          } else {
            totalTip = payment.meta.tip;
          }

          const data = {
            amount: cheque.amount,
            payment: {
              id: order.id,
              order: order.id,
              status: PAYMENT_STATUS.SUCCESS,
              amount: cheque.amount,
              method: payment.method,
              meta: {
                ...payment.meta,
                tip: totalTip,
              },
            },
            order: {
              ...order,
              subPayments,
            },
          };

          /** Handles if the cheque is part of a booking -> then change its status */
          bookings.actions.finish(cheque.id);

          const printArgs = {
            id: order.id,
            order,
            terminal: payment.variant,
            isInvoicePayment:
              ['PRINTED_INVOICE', 'INVOICE'].includes(payment.method) ||
              !!(
                subPayments &&
                subPayments.find((p) => ['PRINTED_INVOICE', 'INVOICE'].includes(p.type))
              ),
            QR: storageData.printQROnCheque ? undefined : `claim:${payment.id}:${cheque.amount}`,
            payment,
          };

          // Receipt counter must be incremented before SYNC_POS_ORDER so that
          // OrderSyncer captures the correct counter in the basketToken
          const maxRetries = 3;
          for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
              await dispatchPrintReceipt(printArgs);
              break;
            } catch (e) {
              logger.error('Failed to print receipt', {
                printError: String(e),
                orderId: order.id,
                attempt,
                maxRetries,
              });
              // Wait before retrying, but don't wait after the last attempt
              if (attempt < maxRetries) {
                await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
              }
            }
          }

          dispatch({
            type: actions.SYNC_POS_ORDER,
            ...data,
          });
        }
      }
    })();
  }, [payment.status]);

  return null;
};

export default PaymentHandler;
